import React, { useEffect, useRef } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { createStructuredSelector } from 'reselect';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import withErrorBoundary from '../../../utils/HOCs/withErrorBoundary';
import useStyles from './style';
import Grid from '../../../components/molecules/Grid';
import Typography from '../../../components/atoms/Typography';
import { updateSearchData } from './redux/actions';
import PopularSearch from '../../../components/organism/PopularSearch';
import { searchData } from './redux/selectors';
import SearchBar from '../SearchBar';
import PopupWithBackground from '../../../components/organism/PopupWithBackground';
import { canUseDOM } from '../../../utils/canUseDOM';
import { getDictionaryText } from '../../../utils/getDictionaryText';
import { COMPONENT_NAME } from './locators';
import {
  getPageName,
  getComponentName,
} from '../../../utils/getPageComponentInfo';
import { gsap } from 'gsap';
import { useMediaQuery } from '@material-ui/core';
import { breakpoints } from '../../../../styles/themes/vad/constants';

const Search = props => {
  const classes = useStyles();
  const { updateSearchData, searchData } = props;
  const pageName = getPageName(props);
  const componentName = getComponentName(props);
  const isMobile = useMediaQuery(`(max-width:${breakpoints.values.lg}px)`);

  const {
    heading,
    image,
    popularSearch,
    searchPlaceholderText,
    backgroundColor,
    navigationLink,
  } = props.fields;
  const closeIconAriaLabel = getDictionaryText('closeIconAriaLabel');
  const backgrooundColorToApply = get(
    backgroundColor,
    'fields.colorCode.value',
    ''
  );
  const onPopularSeachClick = link => {
    if (canUseDOM) {
      const redirectLink = get(link, 'value.href', '');
      const selectedValue = get(link, 'value.text', '');
      let redirectURL = `${window.location.origin}${redirectLink}?searchTerm=${selectedValue}`;
      window.location.href = redirectURL;
    }
  };

  const titleElem = useRef(null),
    popSearchRef = useRef(null),
    popupWithBackgroundRef = useRef(null);
  let haveAnimated = false;

  const animIn = gsap.timeline();
  let txtArea, bgImage, textField, fieldBtnElem, skewDeg = '-3deg', popSearchTitle, popLinksWrapper;
  useEffect(() => {
    if (isMobile) {
      return;
    }
    setTimeout(function () {
      if (searchData.data) {
        if (!txtArea) { txtArea = get(popupWithBackgroundRef, 'current.txtAreaRef.current'); }
        if (!bgImage) { bgImage = get(popupWithBackgroundRef, 'current.bgPicRef.current.ref.current'); }
        if (!popSearchTitle) { popSearchTitle = get(popSearchRef, 'current.popTitleElem.current'); }
        if (!popLinksWrapper) { popLinksWrapper = get(popSearchRef, 'current.popLinksWrapper.current'); }
        if (!textField) { textField = txtArea.querySelector('.text-field'); }
        if (!fieldBtnElem) { fieldBtnElem = txtArea.querySelector('.field-btn'); }
        if (!haveAnimated) {
          if (typeof window !== 'undefined') {
            textField && textField.offsetWidth && window.innerWidth * 0.3 < textField.offsetWidth && (skewDeg = '-1.5deg');
          }
          haveAnimated = true;

          // Fade in img
          bgImage && animIn.fromTo(bgImage, {
            opacity: 0,
            ease: 'power1.out',
            duration: '0.15s',
          }, {
            opacity: 1,
          });

          // Animate in text area
          txtArea && animIn.fromTo(txtArea, {
            opacity: 0,
            y: '-16px',
            ease: 'power1.out',
            duration: '0.15s',
          }, {
            opacity: 1,
            y: '0px',
          }, 0.2);

          // Animate in text field
          animIn.to({ i: 0 }, {
            i: 100,
            duration: '.1s',
            onComplete: () => {
              titleElem && titleElem.current && titleElem.current.animPlay();
            },
          }, 0.2);

          // Animate in search field
          textField && animIn.fromTo(textField, {
            opacity: 0,
            skewY: skewDeg,
            duration: '0.4s',
            ease: 'power1.out',
            y: '-16px',
          }, {
            opacity: 1,
            skewY: '0deg',
            y: '0px',
          }, 0.3);

          // Animate in search field button
          fieldBtnElem && animIn.fromTo(fieldBtnElem, {
            opacity: 0,
            duration: '0.25s',
            ease: 'power1.out',
            y: '-16px',
          }, {
            opacity: 1,
            y: '0px',
          }, 0.45);

          // Animate in popular search title
          animIn.to({ i: 0 }, {
            i: 100,
            duration: '.1s',
            onComplete: () => {
              popSearchTitle && popSearchTitle.animPlay();
            },
          }, 0.45);

          // Animate in popular search links
          popLinksWrapper && animIn.fromTo(popLinksWrapper, {
            opacity: 0,
            duration: '0.5s',
            y: '-16px',
          }, {
            opacity: 1,
            y: '0px',
          }, 0.5);
        }
      }
    }, 100);
  }, [searchData]);

  return (
    <PopupWithBackground
      onCloseClick={updateSearchData}
      shouldOpen={searchData.data}
      backgroundImage={image}
      backgroundColor={backgrooundColorToApply}
      anchor={'top'}
      closeIconAriaLabel={closeIconAriaLabel}
      alternateAltText={heading && heading.value}
      ref={popupWithBackgroundRef}
      isMobile={isMobile}
      isSearch={true}
    >
      <Grid item className={classes.root} data-locator={COMPONENT_NAME}>
        {heading && (
          <Typography
            className={classes.title}
            variant="h2"
            component="h1"
            gutterBottom={true}
            ref={titleElem}
            animateSettings={{
              selfTrigger: false,
              willAnimate: !isMobile,
              rotateReversed: true,
              duration: isMobile ? '0ms' : '250ms',
            }}
          >
            <Text field={heading} />
          </Typography>
        )}
        <Grid item className={classes.searchInput}>
          {searchPlaceholderText && (
            <SearchBar
              className={classes.searchPopupInput}
              ctaClass={classes.popupCTAButton}
              disableClearIcon={true}
              placeHolder={searchPlaceholderText}
              redirectLink={navigationLink && navigationLink.value}
              id={'search_bar_popup'}
              appConfig={props.appConfig}
              animateSettings={{
                willAnimate: !isMobile,
                startDelay: isMobile ? '0ms' : 300,
              }}
            />
          )}
        </Grid>
        <PopularSearch
          popularSearch={popularSearch}
          onPopularSeachClick={onPopularSeachClick}
          pageName={pageName}
          componentName={componentName}
          ref={popSearchRef}
        />
      </Grid>
    </PopupWithBackground>
  );
};

export const mapDispatchToProps = dispatch => {
  return {
    updateSearchData: params => dispatch(updateSearchData(params)),
  };
};

export const mapStateToProps = createStructuredSelector({
  searchData: searchData,
});

export default withErrorBoundary(withSitecoreContext()(
  withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(Search)))
);
